import { z } from 'zod';
import { timestamp, uuid } from './common.js';
import { manuscriptJsonb, documentVersionSchema } from './document.js';
import { jobAccepted } from './document.dto.js';
import { renderFormat } from './job.js';
import { listQueryInput, paginated } from './list.js';
/**
 * Editor (§4, F65/F66/F67 — WS-EDITOR). `document_versions` read/diff (the
 * DAG this slice reads but does not own), F67 autosave + export, F66 reader
 * resume position (`reader_positions`, new in M3), F65 Theme styling.
 */
// --- version.list / version.get / version.diff (F65/F66/F67 shared reads) ----
export const versionSortColumns = ['createdAt'];
/** `version.list` — the editor's version/language switcher + F66 chapter/version history. */
export const versionListQuery = listQueryInput(versionSortColumns, {
    defaultSort: 'createdAt',
    defaultDir: 'desc',
}).extend({ documentId: uuid });
export const versionListResult = paginated(documentVersionSchema);
export const versionGetInput = z.object({ documentId: uuid, versionId: uuid });
// response: documentVersionSchema (./document.js)
export const versionChapterDiffStatus = z.enum(['added', 'removed', 'changed', 'unchanged', 'moved']);
export const versionChapterDiffEntry = z.object({
    chapterId: z.string().min(1),
    title: z.string(),
    status: versionChapterDiffStatus,
    baseOrder: z.number().int().nonnegative().nullable(),
    targetOrder: z.number().int().nonnegative().nullable(),
});
export const versionDiffInput = z.object({
    documentId: uuid,
    baseVersionId: uuid,
    targetVersionId: uuid,
});
/**
 * `version.diff` — the diff BASIS only: which chapters were added/removed/
 * changed/reordered between two DAG versions, not a text/word-level diff (a
 * chapter's `manuscriptChapter.body` diff, if ever needed, is a UI-layer
 * concern built on two already-fetched `version.get` results). Keeps the
 * contract cheap and stable regardless of chapter body size (documents can
 * be unlimited via F2).
 */
export const versionDiffResult = z.object({
    documentId: uuid,
    baseVersionId: uuid,
    targetVersionId: uuid,
    chapters: z.array(versionChapterDiffEntry),
});
// --- editor.save (F67 autosave) ------------------------------------------------
/**
 * `editor.save` — F67 WYSIWYG autosave. Owner + `share_role='editor'`
 * grantees only (server-enforced ACL, never a UI-only gate). Autosave is a
 * SYNCHRONOUS server-side `document_versions` append (`origin='edit'`), NOT
 * a worker job (Area E F67: "Autosave is a server-side write_version append,
 * NOT a worker job") — no moderation gate on private edits either (that
 * gate applies at public share time, F62/F27).
 */
export const editorSaveInput = z.object({
    documentId: uuid,
    /** The version this edit branches from — becomes the new version's `parentVersionId` (DAG). */
    baseVersionId: uuid,
    content: manuscriptJsonb,
    /**
     * Debounce/idempotency key — distinct from the WS envelope `id`. The SAME
     * autosave attempt (client retry after a dropped ack) reuses this id; a
     * new edit batch generates a fresh one. Server dedupes via
     * `document_versions.client_mutation_id`'s partial-unique
     * `(documentId, clientMutationId)`: a replayed save returns the
     * already-created version instead of appending a duplicate.
     */
    clientMutationId: uuid,
});
export const editorSaveResult = documentVersionSchema;
// --- reader.position.get / reader.position.set (F66 resume) -------------------
export const readingPositionValue = z.object({
    chapterId: z.string().min(1),
    offsetPercent: z.number().min(0).max(100),
});
/** One reader's resume position for one document — per-VERSION (F66 AC: "per-VERSION resume position when switching version/language"). */
export const readerPositionSchema = z.object({
    userId: uuid,
    documentId: uuid,
    versionId: uuid,
    position: readingPositionValue,
    updatedAt: timestamp,
});
export const readerPositionGetInput = z.object({ documentId: uuid });
export const readerPositionGetResult = z.object({ position: readerPositionSchema.nullable() });
export const readerPositionSetInput = z.object({
    documentId: uuid,
    versionId: uuid,
    position: readingPositionValue,
});
export const readerPositionSetResult = readerPositionSchema;
/** Push on `user:{userId}` (§5.5) — F66 AC "synced across devices": another tab/device just moved this reader's position. */
export const readerPositionPush = z.object({
    documentId: uuid,
    versionId: uuid,
    position: readingPositionValue,
});
// --- style.apply (F65) ---------------------------------------------------------
/**
 * The fixed 8-name document Theme catalog (F0c/D5). Presentation-only —
 * orthogonal to the app light/dark theme (`users.theme`) and the F66 reader
 * theme (light/sepia/dark/high-contrast); never conflate the three (§4).
 * `'noir'` is the one dark-by-default Theme.
 */
export const documentThemeSlug = z.enum([
    'manuscript',
    'academic',
    'modern',
    'storybook',
    'technical',
    'editorial',
    'minimal',
    'noir',
]);
/** `style.apply` — live-preview-then-commit; this op is the COMMIT (Apply & re-render), never writing a version without one (F65: no moderation gate, pure presentation). Enqueues the existing `render` worker's `apply_theme` stage. */
export const styleApplyInput = z.object({
    documentId: uuid,
    versionId: uuid,
    themeSlug: documentThemeSlug,
});
export const styleApplyResult = jobAccepted;
// --- export.start (F67) ---------------------------------------------------------
/** `export.start` — PDF/DOCX/MD/TXT export via the existing `render` worker; artifact lands in `xdocs-uploads` behind a presigned download link (delivered over `job:{jobId}`, never inline over WS — §5.7 "large artifacts never over WS"). */
export const exportStartInput = z.object({
    documentId: uuid,
    versionId: uuid,
    format: renderFormat,
});
export const exportStartResult = jobAccepted;
